import {ref, computed} from 'vue'
import {defineStore, acceptHMRUpdate} from 'pinia'
import api from "../services/api.js";

export const useAuthStore = defineStore('auth', {

    state: () => ({
        _user: null,
        _theme: null,
        _home_stats: null,
        _notifications: [],
        _unread_notifications_count: null
    }),

    getters: {
        user: (state) => state._user,
        theme: (state) => state._theme,
        home_stats: (state) => state._home_stats,
        notifications: (state) => state._notifications,
        unread_notifications_count: (state) => state._unread_notifications_count,

        // Mirrors the role:internal-super-admin|internal-team-leader middleware on
        // BulkSendController. A mistake here messages hundreds of clients, so agents
        // are deliberately excluded.
        canBulkSend: (state) => {
            const allowed = ['internal-super-admin', 'internal-team-leader']

            return (state._user?.roles ?? []).some(role => role?.name && allowed.includes(role.name))
        },

        // Mirrors the team leader / super admin restriction on ChatController::stats.
        canViewChatStats: (state) => {
            const allowed = ['internal-super-admin', 'internal-team-leader']

            return (state._user?.roles ?? []).some(role => role?.name && allowed.includes(role.name))
        }
    },

    actions: {
        async login(credentials) {
            this._user = null
            await api.getSactumCookie()
            const response = await api.post('auth/login', credentials)
            this._user = response.data.user
        },
        async getUser(credentials) {
            const response = await api.get('user')
            this._user = response.data
            this._theme = this._user.settings?.theme ?? 'light'
        },
        async logout() {
            await api.post('auth/logout')
            this._user = null
            localStorage.removeItem('auth')
            window.location.href = '#/auth/login'
        },
        async updateTheme(theme) {
            // await api.post('user/settings/theme', {theme: theme})
            this._theme = theme
        },
        async getHomeStats() {
            const res = await api.get('/auth/home-stats')
            this._home_stats = res.data
        },
        async getNotifications() {
            const res = await api.get('/notifications')
            this._notifications = res.data.notifications
            this._unread_notifications_count = res.data.unread_notifications_count
        },
        async markNotificationAsRead(notificationId) {
            const res = await api.post(`/notifications/${notificationId}/mark-as-read`)
            this._notifications = res.data.notifications
            this._unread_notifications_count = res.data.unread_notifications_count
        },
        async markAllNotificationsAsRead() {
            const res = await api.post(`/notifications/mark-all-read`)
            this._notifications = res.data.notifications
            this._unread_notifications_count = res.data.unread_notifications_count
        },
        async clearAll(notification) {
            const res = await api.post(`/notifications/clear-all`)
            this._notifications = res.data.notifications
            this._unread_notifications_count = res.data.unread_notifications_count
        },
        isAdmin() {
            let is_admin = false;
            if (!this._user) {
                return is_admin;
            }

            const admin_roles = ['super-admin', 'internal-super-admin', 'internal-team-leader']
            is_admin = this._user.roles.some(role => {
                return role?.name && admin_roles.includes(role.name);
            });

            return is_admin;
        }
    },
    persist: {
        storage: localStorage
    },
})

if (import.meta.hot) {
    import.meta.hot.accept(acceptHMRUpdate(useAuthStore, import.meta.hot))
}
